// utils/notificationsDb.js
// ─────────────────────────────────────────────────────────────────────────
// Single source of truth for how notifications are written to and read from
// Firebase. Every write (creating, marking read, deleting) and every paginated
// read goes through here so the fan-out to the secondary indexes below never
// drifts out of sync.
//
// DATA MODEL (see migration-and-db-notes.md for the full rationale):
//
//   Notifications/{userId}/{notifId}
//     → full notification object. Source of truth. Kept so any code that
//       still reads/writes a single notification by id (e.g. deep links)
//       keeps working unchanged.
//
//   NotificationsByCategory/{userId}/{category}/{notifId}
//     → duplicate of the same object, fanned out into one of four buckets:
//       'follow' | 'reaction' | 'messages' | 'general'.
//       This is what each tab in NotificationsPage queries directly, with
//       orderByChild('timestamp') + limitToLast/endAt, so a tab never has to
//       download notifications belonging to the other three tabs.
//
//   NotificationsByType/{userId}/{type}/{notifId}
//     → duplicate of the same object, fanned out by exact notification
//       `type` (post, comment, reply, team_request, ...). Used only when the
//       "general" tab's dropdown filter narrows to one specific type, so
//       that filter is a direct query too instead of a client-side .filter()
//       over an already-downloaded page.
//
//   NotificationsUnreadCount/{userId}/{category}   (+ /total)
//     → running integer counters, maintained with server-side atomic increments on
//       every create / mark-read / delete. Lets the UI show unread badges
//       with a single tiny read instead of downloading notifications to
//       count them.
//
// `read` means the notification has stopped contributing to badge counters.
// `highlighted` is independent and remains true until the notification is
// opened (or the user explicitly marks the category as read).
//
// Every write below is a single multi-location `update()` call, so the three
// copies of a notification and their state flags move atomically together.
// ─────────────────────────────────────────────────────────────────────────

import { firebase } from './firebase-config';

export const NOTIF_CATEGORIES = ['follow', 'reaction', 'messages', 'general'];

const MESSAGE_TYPES = new Set(['chat_message', 'private_message', 'group_message', 'saraha']);

// type → tab category. Anything not listed explicitly falls into 'general',
// which mirrors the tab grouping NotificationsPage already used.
export function getNotificationCategory(type) {
  if (type === 'follow') return 'follow';
  if (type === 'reaction') return 'reaction';
  if (MESSAGE_TYPES.has(type)) return 'messages';
  return 'general';
}

function changeUnreadCounter(userId, key, delta) {
  const ref = firebase.database().ref(`NotificationsUnreadCount/${userId}/${key}`);
  const operation = delta > 0
    // Cross-user notification senders cannot read the recipient's private
    // counter. ServerValue.increment performs the atomic write without a read.
    ? ref.set(firebase.database.ServerValue.increment(delta))
    // Counter reductions are performed by the owner, who has read access.
    : ref.transaction((value) => Math.max(0, (Number(value) || 0) + delta));

  return operation.catch((error) => {
    console.warn(`Unread counter sync failed (${userId}/${key}):`, error);
    return null;
  });
}

function bumpUnread(userId, category, delta) {
  // Best-effort: notification delivery must not fail because a badge counter
  // could not be synchronized.
  return Promise.allSettled([
    changeUnreadCounter(userId, 'total', delta),
    changeUnreadCounter(userId, category, delta),
  ]);
}

/**
 * Create a notification. Fans out to Notifications/, NotificationsByCategory/
 * and NotificationsByType/ in one atomic update, then bumps unread counters.
 * Returns the new notification id.
 */
export async function writeNotification(userId, notificationData) {
  if (!userId || !notificationData?.type) return null;

  const category = getNotificationCategory(notificationData.type);
  const notifId = firebase.database().ref(`Notifications/${userId}`).push().key;

  const payload = {
    ...notificationData,
    timestamp: notificationData.timestamp || Date.now(),
    read: false,
    highlighted: true,
  };

  const updates = {
    [`Notifications/${userId}/${notifId}`]: payload,
    [`NotificationsByCategory/${userId}/${category}/${notifId}`]: payload,
    [`NotificationsByType/${userId}/${payload.type}/${notifId}`]: payload,
  };

  await firebase.database().ref().update(updates);
  bumpUnread(userId, category, 1);

  return notifId;
}

/** Mark a single notification as read across all three copies. */
export async function markNotificationRead(userId, notification) {
  if (!userId || !notification?.id) return;

  const category = getNotificationCategory(notification.type);
  const updates = {
    [`Notifications/${userId}/${notification.id}/read`]: true,
    [`Notifications/${userId}/${notification.id}/highlighted`]: false,
    [`NotificationsByCategory/${userId}/${category}/${notification.id}/read`]: true,
    [`NotificationsByCategory/${userId}/${category}/${notification.id}/highlighted`]: false,
    [`NotificationsByType/${userId}/${notification.type}/${notification.id}/read`]: true,
    [`NotificationsByType/${userId}/${notification.type}/${notification.id}/highlighted`]: false,
  };

  await firebase.database().ref().update(updates);
  if (!notification.read) bumpUnread(userId, category, -1);
}

/** Mark a notification unread across all three copies. */
export async function markNotificationUnread(userId, notification) {
  if (!userId || !notification?.id) return;

  const category = getNotificationCategory(notification.type);
  const updates = {
    [`Notifications/${userId}/${notification.id}/read`]: false,
    [`Notifications/${userId}/${notification.id}/highlighted`]: true,
    [`NotificationsByCategory/${userId}/${category}/${notification.id}/read`]: false,
    [`NotificationsByCategory/${userId}/${category}/${notification.id}/highlighted`]: true,
    [`NotificationsByType/${userId}/${notification.type}/${notification.id}/read`]: false,
    [`NotificationsByType/${userId}/${notification.type}/${notification.id}/highlighted`]: true,
  };

  await firebase.database().ref().update(updates);
  if (notification.read) bumpUnread(userId, category, 1);
}

/** Mark a batch of notifications (e.g. a whole tab) as read in one update. */
export async function markManyRead(userId, notifications) {
  const unread = notifications.filter((n) => !n.read);
  if (!userId || unread.length === 0) return;

  const updates = {};
  const perCategory = {};
  unread.forEach((n) => {
    const category = getNotificationCategory(n.type);
    perCategory[category] = (perCategory[category] || 0) + 1;
    updates[`Notifications/${userId}/${n.id}/read`] = true;
    updates[`Notifications/${userId}/${n.id}/highlighted`] = false;
    updates[`NotificationsByCategory/${userId}/${category}/${n.id}/read`] = true;
    updates[`NotificationsByCategory/${userId}/${category}/${n.id}/highlighted`] = false;
    updates[`NotificationsByType/${userId}/${n.type}/${n.id}/read`] = true;
    updates[`NotificationsByType/${userId}/${n.type}/${n.id}/highlighted`] = false;
  });

  await firebase.database().ref().update(updates);

  changeUnreadCounter(userId, 'total', -unread.length);
  Object.entries(perCategory).forEach(([category, count]) => {
    changeUnreadCounter(userId, category, -count);
  });
}

/** Delete a single notification from all three copies. */
export async function deleteNotificationEverywhere(userId, notification) {
  if (!userId || !notification?.id) return;

  const category = getNotificationCategory(notification.type);
  const updates = {
    [`Notifications/${userId}/${notification.id}`]: null,
    [`NotificationsByCategory/${userId}/${category}/${notification.id}`]: null,
    [`NotificationsByType/${userId}/${notification.type}/${notification.id}`]: null,
  };

  await firebase.database().ref().update(updates);
  if (!notification.read) bumpUnread(userId, category, -1);
}

/** Delete a batch of notifications (e.g. "delete all" on a tab). */
export async function deleteManyNotifications(userId, notifications) {
  if (!userId || notifications.length === 0) return;

  const updates = {};
  let unreadTotal = 0;
  const unreadPerCategory = {};

  notifications.forEach((n) => {
    const category = getNotificationCategory(n.type);
    updates[`Notifications/${userId}/${n.id}`] = null;
    updates[`NotificationsByCategory/${userId}/${category}/${n.id}`] = null;
    updates[`NotificationsByType/${userId}/${n.type}/${n.id}`] = null;
    if (!n.read) {
      unreadTotal += 1;
      unreadPerCategory[category] = (unreadPerCategory[category] || 0) + 1;
    }
  });

  await firebase.database().ref().update(updates);

  if (unreadTotal > 0) {
    changeUnreadCounter(userId, 'total', -unreadTotal);
    Object.entries(unreadPerCategory).forEach(([category, count]) => {
      changeUnreadCounter(userId, category, -count);
    });
  }
}

const CHUNK_SIZE = 400; // stay well under RTDB's per-update path limits

async function chunkedUpdate(updates) {
  const paths = Object.keys(updates);
  for (let i = 0; i < paths.length; i += CHUNK_SIZE) {
    const chunk = {};
    paths.slice(i, i + CHUNK_SIZE).forEach((p) => { chunk[p] = updates[p]; });
    await firebase.database().ref().update(chunk);
  }
}

/**
 * Explicitly mark every notification in a whole category (or a single
 * general-filter type) as read and remove all highlights, including rows
 * whose badge count was already acknowledged when the tab was opened.
 */
export async function markAllReadInCategory(userId, { category, type }) {
  if (!userId) return;
  const path = type ? `NotificationsByType/${userId}/${type}` : `NotificationsByCategory/${userId}/${category}`;
  const snap = await firebase.database().ref(path).once('value');
  const data = snap.val();
  if (!data) return;

  const all = Object.entries(data).map(([id, value]) => ({ id, ...value }));
  const unread = all.filter((n) => !n.read);
  const highlighted = all.filter((n) => n.highlighted ?? !n.read);
  if (unread.length === 0 && highlighted.length === 0) return [];

  const updates = {};
  all.forEach((n) => {
    const cat = type ? getNotificationCategory(n.type) : category;
    updates[`Notifications/${userId}/${n.id}/read`] = true;
    updates[`Notifications/${userId}/${n.id}/highlighted`] = false;
    updates[`NotificationsByCategory/${userId}/${cat}/${n.id}/read`] = true;
    updates[`NotificationsByCategory/${userId}/${cat}/${n.id}/highlighted`] = false;
    updates[`NotificationsByType/${userId}/${n.type}/${n.id}/read`] = true;
    updates[`NotificationsByType/${userId}/${n.type}/${n.id}/highlighted`] = false;
  });
  await chunkedUpdate(updates);

  const perCategory = {};
  unread.forEach((n) => {
    const cat = type ? getNotificationCategory(n.type) : category;
    perCategory[cat] = (perCategory[cat] || 0) + 1;
  });
  changeUnreadCounter(userId, 'total', -unread.length);
  Object.entries(perCategory).forEach(([cat, count]) => {
    changeUnreadCounter(userId, cat, -count);
  });

  return all.map((n) => n.id);
}

/**
 * Acknowledge a tab for badge-count purposes while keeping its rows visually
 * highlighted until the user opens them or explicitly marks all as read.
 */
export async function acknowledgeCategoryCount(userId, { category, type }) {
  if (!userId) return [];
  const path = type ? `NotificationsByType/${userId}/${type}` : `NotificationsByCategory/${userId}/${category}`;
  const snap = await firebase.database().ref(path).orderByChild('read').equalTo(false).once('value');
  const data = snap.val();
  if (!data) return [];

  const unread = Object.entries(data).map(([id, value]) => ({ id, ...value }));
  const updates = {};
  unread.forEach((n) => {
    const cat = type ? getNotificationCategory(n.type) : category;
    updates[`Notifications/${userId}/${n.id}/read`] = true;
    updates[`Notifications/${userId}/${n.id}/highlighted`] = true;
    updates[`NotificationsByCategory/${userId}/${cat}/${n.id}/read`] = true;
    updates[`NotificationsByCategory/${userId}/${cat}/${n.id}/highlighted`] = true;
    updates[`NotificationsByType/${userId}/${n.type}/${n.id}/read`] = true;
    updates[`NotificationsByType/${userId}/${n.type}/${n.id}/highlighted`] = true;
  });
  await chunkedUpdate(updates);

  const perCategory = {};
  unread.forEach((n) => {
    const cat = type ? getNotificationCategory(n.type) : category;
    perCategory[cat] = (perCategory[cat] || 0) + 1;
  });
  changeUnreadCounter(userId, 'total', -unread.length);
  Object.entries(perCategory).forEach(([cat, count]) => {
    changeUnreadCounter(userId, cat, -count);
  });

  return unread.map((n) => n.id);
}

/**
 * Delete every notification in a whole category (or a single general-filter
 * type) — not just the page currently loaded in the UI.
 */
export async function deleteAllInCategory(userId, { category, type }) {
  if (!userId) return;
  const path = type ? `NotificationsByType/${userId}/${type}` : `NotificationsByCategory/${userId}/${category}`;
  const snap = await firebase.database().ref(path).once('value');
  const data = snap.val();
  if (!data) return [];

  const all = Object.entries(data).map(([id, value]) => ({ id, ...value }));
  if (all.length === 0) return [];

  const updates = {};
  let unreadTotal = 0;
  const unreadPerCategory = {};
  all.forEach((n) => {
    const cat = type ? getNotificationCategory(n.type) : category;
    updates[`Notifications/${userId}/${n.id}`] = null;
    updates[`NotificationsByCategory/${userId}/${cat}/${n.id}`] = null;
    updates[`NotificationsByType/${userId}/${n.type}/${n.id}`] = null;
    if (!n.read) {
      unreadTotal += 1;
      unreadPerCategory[cat] = (unreadPerCategory[cat] || 0) + 1;
    }
  });
  await chunkedUpdate(updates);

  if (unreadTotal > 0) {
    changeUnreadCounter(userId, 'total', -unreadTotal);
    Object.entries(unreadPerCategory).forEach(([cat, count]) => {
      changeUnreadCounter(userId, cat, -count);
    });
  }

  return all.map((n) => n.id);
}

/**
 * Build a query for one page of one tab (or one general-filter sub-type).
 * Pass `type` to query NotificationsByType instead of NotificationsByCategory
 * (used only by the "general" tab's dropdown filter).
 * Pass `beforeTimestamp` (the oldest timestamp already loaded) to page
 * further back in time.
 */
export function getNotificationsPageQuery({ userId, category, type, pageSize, beforeTimestamp }) {
  const path = type
    ? `NotificationsByType/${userId}/${type}`
    : `NotificationsByCategory/${userId}/${category}`;

  let query = firebase.database().ref(path).orderByChild('timestamp');
  query = beforeTimestamp != null
    ? query.endAt(beforeTimestamp - 1).limitToLast(pageSize)
    : query.limitToLast(pageSize);
  return query;
}

/** Snapshot → sorted (newest-first) array of {id, ...data}. */
export function snapshotToSortedArray(snapshot) {
  const data = snapshot.val();
  if (!data) return [];
  return Object.entries(data)
    .map(([id, value]) => ({ id, ...value }))
    .sort((a, b) => b.timestamp - a.timestamp);
}

/** Live listener for the unread-count node. Returns an unsubscribe fn. */
export function subscribeUnreadCounts(userId, callback) {
  if (!userId) return () => {};
  const ref = firebase.database().ref(`NotificationsUnreadCount/${userId}`);
  const handler = (snap) => callback(snap.val() || { total: 0 });
  ref.on('value', handler);
  return () => ref.off('value', handler);
}
